import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import { useGetApi, usePostApi } from "src/hooks";
import { getPdfBody } from "src/pages/Reports/Report/Report.utils";
import {
  type ISearchEntry,
  type ISearchFormWithUndefined,
} from "src/pages/SearchPage/SearchPage.types";
import { getParams } from "src/pages/SearchPage/SearchPage.utils";
import { useDispatch } from "src/redux/hooks";
import { setUsers } from "src/redux/slices/entriesGeneratorSlice";

import Entry from "../../shared/EntriesGenerator/Entry";
import { MEDLBID } from "../../../utils";

interface Props {
  filters: ISearchFormWithUndefined;
  disabled: boolean;
}

const PDFButton = ({ filters, disabled }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { collectionId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const mapSearchResults = useCallback(
    (data: any) => {
      dispatch(setUsers(data.users));
      if (!data.data) return [];
      return data.data.map((entry: any) => ({
        ...entry,
        lbentry:
          typeof entry.lbentry === "string"
            ? JSON.parse(entry.lbentry)
            : entry.lbentry,
      })) as ISearchEntry[];
    },
    [dispatch],
  );

  const { data, refetch } = useGetApi({
    request: "entry-text-search",
    fetchOnInit: false,
    mapper: mapSearchResults,
  });

  const { post } = usePostApi();

  const handleGetPdfData = async () => {
    setIsLoading(true);
    const { type, ...restFilters } = filters;
    const params: Record<string, any> = {
      pdf: true,
      ...getParams(restFilters),
    };
    if (!type) {
      params.globalSearch = true;
    } else if (type === "colid") {
      params.isCol = true;
      params.colid = collectionId;
    } else if (["lbeid", "meid"].includes(type)) {
      params.lbid = collectionId;
    }
    await refetch({ params });

    await new Promise(resolve => setTimeout(resolve, 0));

    if (ref.current) {
      const body = await getPdfBody(ref.current);
      const fileData = await post({
        request: "create-search-pdf",
        body: {
          params: {
            body,
            name: `Search_Results_${dayjs().format("D_MMM_YYYY")}`,
            ...(collectionId && searchParams.get("type") === "meid"
              ? { scale: 0.45 }
              : {}),
          },
        },
      });
      window.open(fileData.file, "_blank");
    }

    setIsLoading(false);
  };

  const renderEntryTitle = useCallback((entry: ISearchEntry) => {
    return () => (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}>
        <p style={{ margin: 0 }}>{`${entry.lbname}`}</p>

        <p style={{ margin: 0 }}>
          {entry.mention && `Mention: ${entry.mention}`}
        </p>
      </div>
    );
  }, []);

  const containsMedicalEntry = useMemo(
    () => data?.some(entry => entry.lbid === MEDLBID),
    [data],
  );

  return (
    <>
      <Button
        onClick={handleGetPdfData}
        disabled={disabled}
        loading={isLoading}>
        Generate PDF
      </Button>
      <div ref={ref}>
        <div
          id="entries"
          style={{
            display: "none",
            width: "100%",
            minWidth: containsMedicalEntry ? "1600px" : "500px",
          }}>
          {data?.map((entry: ISearchEntry, index: number) => (
            <Entry
              key={index}
              entry={entry}
              renderTitle={renderEntryTitle(entry)}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default PDFButton;
